import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '@/contexts/CartContext'
import { Button } from '@/components/ui/button'
import { Trash2, Plus, Minus, ArrowRight } from 'lucide-react'
import { gtmEvents } from '@/lib/gtm'

const Cart = () => {
  const { items, removeFromCart, updateQuantity, cartTotal, itemsCount } =
    useCart()
  const navigate = useNavigate()
  const shipping = itemsCount > 0 ? 15.0 : 0
  const total = cartTotal + shipping

  const handleCheckout = () => {
    gtmEvents.beginCheckout(
      items.map((item) => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        item_category: item.category,
      })),
      total,
    )
    navigate('/checkout')
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Seu Carrinho</h1>

      {items.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border">
          <p className="text-lg text-muted-foreground mb-6">
            Seu carrinho está vazio.
          </p>
          <Button asChild>
            <Link to="/shop">Continuar Comprando</Link>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Cart Items */}
          <div className="flex-1 space-y-4">
            {items.map((item) => (
              <div
                key={`${item.id}-${item.size}-${item.color}`}
                className="flex gap-4 p-4 bg-white rounded-lg border items-center"
              >
                <div className="h-24 w-24 bg-slate-50 rounded-md overflow-hidden flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold">{item.name}</h3>
                  <div className="text-sm text-muted-foreground">
                    {item.size && (
                      <span className="mr-2">Tam: {item.size}</span>
                    )}
                    {item.color && <span>Cor: {item.color}</span>}
                  </div>
                  <div className="font-medium text-primary mt-1">
                    R$ {item.price.toFixed(2).replace('.', ',')}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center border rounded-md h-8">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="px-2 hover:bg-slate-100 h-full"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="px-2 text-sm">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="px-2 hover:bg-slate-100 h-full"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 text-sm hover:underline flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" /> Remover
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="w-full lg:w-80 h-fit bg-white p-6 rounded-lg border shadow-sm">
            <h2 className="text-xl font-bold mb-6">Resumo</h2>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span>Subtotal ({itemsCount} itens)</span>
                <span>R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Frete Estimado</span>
                <span>R$ {shipping.toFixed(2).replace('.', ',')}</span>
              </div>
              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span>R$ {total.toFixed(2).replace('.', ',')}</span>
              </div>
            </div>
            <Button className="w-full" size="lg" onClick={handleCheckout}>
              Finalizar Compra <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Cart
